// Check A của doctor: "workspace Avatar nằm ở thư mục con" — xem
// `detect-nested-avatar-workspace-layout.ts` để biết cơ chế hỏng.
//
// Tách khỏi `doctor.ts` (đang 561 dòng) theo đúng tiền lệ `telemetry-delivery-probe.ts`:
// `runChecks` không export nên check nào nằm trong nó là check KHÔNG có test.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import type { CheckResult } from "../commands/doctor.js";
import { countQueuedTelemetryEvents } from "./count-queued-telemetry-events.js";
import {
  detectNestedAvatarWorkspaces,
  type NestedWorkspaceCandidate,
} from "./detect-nested-avatar-workspace-layout.js";
import { pathExists } from "./filesystem-helpers.js";
import {
  type NestedWorkspaceRelocationPlan,
  planNestedWorkspaceRelocation,
  RELOCATABLE_SCAFFOLD_ENTRIES,
} from "./plan-nested-workspace-relocation.js";
import { relocateNestedAvatarWorkspace } from "./relocate-nested-avatar-workspace.js";
import { log } from "./terminal-logger.js";
import { listEnabledTools } from "./tool-state-store.js";
import { DEFAULT_REGISTRY_PATH } from "./workspace-registry-store.js";

const CHECK_NAME = "Vị trí workspace (hook/telemetry)";

/** Hệ quả phải nằm NGAY trong thông điệp — người đọc không tự suy ra được. */
const CONSEQUENCE =
  "mở Claude Code ở thư mục hiện tại thì hook và telemetry KHÔNG chạy (Claude Code chỉ nạp .claude/settings.json của đúng thư mục được mở, không dò xuống thư mục con)";

async function readDirOrEmpty(path: string): Promise<string[]> {
  try {
    return (await fs.readdir(path)).sort();
  } catch {
    return [];
  }
}

async function measureFacts(cwd: string, candidate: NestedWorkspaceCandidate) {
  const [srcEntries, notesEntries, queued, hasSettingsLocal, enabledTools] = await Promise.all([
    readDirOrEmpty(join(candidate.path, "src")),
    readDirOrEmpty(join(candidate.path, "notes")),
    countQueuedTelemetryEvents(candidate.path),
    pathExists(join(candidate.path, ".claude", "settings.local.json")),
    listEnabledTools(candidate.path).catch(() => [] as string[]),
  ]);

  const cwdCollisions: string[] = [];
  for (const entry of RELOCATABLE_SCAFFOLD_ENTRIES) {
    if (candidate.entries.includes(entry) && (await pathExists(join(cwd, entry)))) {
      cwdCollisions.push(entry);
    }
  }

  return {
    childName: candidate.name,
    childEntries: candidate.entries,
    srcEntries,
    notesEntries,
    queuedTelemetryEvents: queued,
    hasSettingsLocal,
    enabledTools,
    cwdCollisions,
  };
}

function describe(
  candidate: NestedWorkspaceCandidate,
  plan: NestedWorkspaceRelocationPlan,
): string {
  const head = `workspace Avatar nằm ở ./${candidate.name} — ${CONSEQUENCE}.`;
  if (plan.action === "relocate") {
    return `${head} Chạy 'avatar doctor --fix' để dời lên thư mục hiện tại (${plan.entriesToMove.join(", ")}).`;
  }
  return `${head} KHÔNG tự dời được: ${plan.manualInstructions.join(" | ")}`;
}

/**
 * Dựng check A. LUÔN trả một `CheckResult` (kể cả ca bình thường) — im lặng khi mọi
 * thứ ổn thì người dùng không có cách nào biết doctor ĐÃ nhìn vào chỗ này.
 *
 * `registryPath` tiêm được để e2e/unit không ghi vào `~/.avatar` thật.
 */
export async function buildNestedWorkspaceRelocationCheck(
  cwd: string,
  registryPath: string = DEFAULT_REGISTRY_PATH,
): Promise<CheckResult> {
  const candidates = await detectNestedAvatarWorkspaces(cwd);

  if (candidates.length === 0) {
    return {
      name: CHECK_NAME,
      status: "ok",
      detail: "không thấy workspace Avatar nào nằm ở thư mục con",
      fixable: false,
    };
  }

  // Nhiều workspace con ⇒ KHÔNG đoán cái nào là cái người dùng muốn. Báo đủ, để người chọn.
  if (candidates.length > 1) {
    return {
      name: CHECK_NAME,
      status: "warn",
      detail: `có ${String(candidates.length)} workspace Avatar ở thư mục con (${candidates.map((c) => `./${c.name}`).join(", ")}) — ${CONSEQUENCE}. Avatar KHÔNG tự chọn hộ: mở Claude Code ĐÚNG thư mục workspace muốn dùng, hoặc tự dời một cái lên đây.`,
      fixable: false,
    };
  }

  const candidate = candidates[0] as NestedWorkspaceCandidate;
  const plan = planNestedWorkspaceRelocation(await measureFacts(cwd, candidate));
  const detail = describe(candidate, plan);

  if (plan.action !== "relocate") {
    // KHÔNG set `fix`: `applyFixes` in "Fixed: ..." mỗi khi `fix()` resolve, nên nhánh
    // không-sửa-được phải VẮNG MẶT khỏi vòng fix chứ không phải có một `fix()` rỗng.
    return { name: CHECK_NAME, status: "warn", detail, fixable: false };
  }

  return {
    name: CHECK_NAME,
    status: "warn",
    detail,
    fixable: true,
    fix: async () => {
      // LIỆT KÊ trước khi động vào đĩa — người dùng thấy đúng thứ sắp bị dời.
      log.info(`  Dời workspace ./${candidate.name} lên thư mục hiện tại:`);
      for (const line of plan.inventory) log.dim(`    · ${line}`);

      const result = await relocateNestedAvatarWorkspace(
        cwd,
        candidate.path,
        plan.entriesToMove,
        registryPath,
      );
      log.dim(`    · bản sao trước khi dời: ${result.backupDir}`);
      log.dim(`    · vỏ rỗng còn lại, xoá bằng tay nếu muốn: rmdir ./${candidate.name}`);
      // Nói thẳng giới hạn: event của những ngày hook không chạy KHÔNG lấy lại được.
      log.dim("    · lưu ý: event của những ngày hook chưa chạy KHÔNG khôi phục được.");
    },
  };
}
